/**
 * Turning the targeted-practice engine's output into the two things a learner
 * can be shown.
 *
 * Kept dependency-free from Relay's generated types for the same reason
 * `sessions.ts` and `debrief.ts` are: this is the testable seam, and a plain
 * `node --test` run has no loader for `.tsx` and no Relay runtime.
 *
 * The spec's §3 is the whole point of this module. `KidProgress` has no field
 * capable of carrying a deficit -- not the learner's wrong words, not an error
 * kind, not a count of anything that went badly. That is PRD §8.2 enforced by
 * the type rather than by a render that stays careful. A carefully-worded
 * component is one edit from leaking; a type with no such field is not.
 */
import { t, type PlainKey } from '../i18n/t.ts';
import { startOfDay } from './sessions.ts';

export type Exemplar = { learnerForm: string; targetForm: string };

export type PracticeItemRow = {
  id: string;
  pattern: string;
  /** `String!` on the schema, not `ErrorKind!`. Never switched on. */
  kind: string;
  occurrences: number;
  lastPractisedAt?: string | null;
  exemplar?: Exemplar | null;
};

export type OutcomeRow = {
  /** `TARGETED` | `RECURRED` | `USED_CORRECTLY`. */
  outcome: string;
  item: {
    id: string;
    pattern: string;
    exemplar?: { targetForm: string } | null;
  };
};

export type ProgressSession = {
  id: string;
  startedAt: string;
  practice: readonly OutcomeRow[];
};

export type Win = { itemId: string; phrase: string };

export type Struggle = {
  id: string;
  pattern: string;
  kind: string;
  occurrences: number;
  lastPractisedAt?: string | null;
  learnerForm: string | null;
  targetForm: string | null;
  /** The most recent outcome's label, or `null` when there is none. */
  status: string | null;
};

export type KidProgress = {
  /**
   * How many of the *fetched* sessions there were.
   *
   * Bounded by the query's `first: 20`, so this is "recent conversations" and
   * the screens must say so. A learner with fifty sessions being told they
   * have had twenty is a number they can disprove.
   */
  conversations: number;
  streak: number;
  phrases: string[];
};

export type AdultProgress = {
  conversations: number;
  streak: number;
  phrases: string[];
  /** The top `WORKING_ON_CAP` of `struggles()`, not all of them. */
  struggles: Struggle[];
  /** How many fetched struggles the cap left out; the screen links to `/practice` for them. */
  moreStruggles: number;
};

/**
 * How many phrases a child is shown.
 *
 * A wall of them stops reading as an achievement and starts reading as a list.
 */
const KID_PHRASE_CAP = 8;

/**
 * How many practice items the progress overview shows.
 *
 * The practice hub lists every one, with filters and "Practise this"; the
 * overview's job is only to say what is being worked on. Twenty cards here
 * were most of the screen's length, and a second copy of the hub's list.
 */
export const WORKING_ON_CAP = 3;

const STATUS_LABEL: Partial<Record<string, PlainKey>> = {
  USED_CORRECTLY: 'status-used-correctly',
  TARGETED: 'status-targeted',
  RECURRED: 'status-recurred',
};

/** A practice outcome as a badge says it, or `null` for one this build does not know. */
export function statusLabel(outcome: string): string | null {
  const key = STATUS_LABEL[outcome];
  return key ? t(key) : null;
}

/** Newest first, by the session's own start. */
function byNewest(sessions: readonly ProgressSession[]): ProgressSession[] {
  return [...sessions].sort((a, b) => Date.parse(b.startedAt) - Date.parse(a.startedAt));
}

/**
 * Below this many consecutive days, there is no streak to show.
 *
 * Design §7: "a single day yields no streak" -- calling one conversation a
 * streak the first time a child (or adult) opens the screen is a claim they
 * can disprove by remembering yesterday. Lives here, not in a component's
 * render, so the rule is exercised by `node --test` rather than only by
 * whichever `.tsx` happened to call this last.
 */
export const MIN_STREAK_DAYS = 2;

/**
 * Consecutive local calendar days ending today or yesterday, or 0 below
 * `MIN_STREAK_DAYS`.
 *
 * Yesterday counts as the end, because a learner opening the app in the
 * morning has not yet had today's conversation and showing them a broken
 * streak for that is both wrong and discouraging.
 *
 * The cursor walks with `setDate`, not by subtracting 86,400,000 ms: across a
 * daylight-saving boundary a day is not 24 hours, and the arithmetic version
 * silently loses or repeats a day twice a year.
 *
 * Bounded by however many sessions the caller fetched, same as
 * `conversations` above: this only ever sees the fetched window, not the
 * learner's whole history, so two conversations a day for forty days and a
 * `first: 20` fetch reads back as "10 days in a row" rather than 40. Widening
 * the fetch (or querying a purpose-built aggregate) to fix that is a product
 * decision not taken here.
 */
export function conversationStreak(sessions: readonly { startedAt: string }[], now: Date): number {
  const days = new Set<number>();
  for (const s of sessions) {
    const at = new Date(s.startedAt);
    if (!Number.isNaN(at.getTime())) days.add(startOfDay(at));
  }
  if (days.size === 0) return 0;

  const cursor = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (!days.has(cursor.getTime())) {
    cursor.setDate(cursor.getDate() - 1);
    if (!days.has(cursor.getTime())) return 0;
  }
  let streak = 0;
  while (days.has(cursor.getTime())) {
    streak += 1;
    cursor.setDate(cursor.getDate() - 1);
  }
  return streak >= MIN_STREAK_DAYS ? streak : 0;
}

/**
 * Phrases the learner has since used correctly, newest session first.
 *
 * One per practice item: the same pattern won in three sessions is one thing
 * learned, not three. An outcome with no exemplar is dropped rather than
 * rendered as an empty chip.
 */
export function wins(sessions: readonly ProgressSession[]): Win[] {
  const seen = new Set<string>();
  const out: Win[] = [];
  for (const session of byNewest(sessions)) {
    for (const row of session.practice) {
      if (row.outcome !== 'USED_CORRECTLY') continue;
      const phrase = row.item.exemplar?.targetForm;
      if (!phrase || seen.has(row.item.id)) continue;
      seen.add(row.item.id);
      out.push({ itemId: row.item.id, phrase });
    }
  }
  return out;
}

/**
 * Live practice items, most recurrent first, each carrying how it last went.
 *
 * The status comes from the sessions fetched alongside, because there is no
 * `PracticeItem.outcomes` field to ask. That bounds it: a pattern last
 * practised outside the fetched window shows no status rather than a stale
 * one.
 */
export function struggles(items: readonly PracticeItemRow[], sessions: readonly ProgressSession[]): Struggle[] {
  const latest = new Map<string, string>();
  for (const session of byNewest(sessions)) {
    for (const row of session.practice) {
      if (!latest.has(row.item.id)) latest.set(row.item.id, row.outcome);
    }
  }
  return [...items]
    .sort((a, b) => b.occurrences - a.occurrences)
    .map((item) => ({
      id: item.id,
      pattern: item.pattern,
      kind: item.kind,
      occurrences: item.occurrences,
      lastPractisedAt: item.lastPractisedAt,
      learnerForm: item.exemplar?.learnerForm ?? null,
      targetForm: item.exemplar?.targetForm ?? null,
      status: statusLabel(latest.get(item.id) ?? ''),
    }));
}

/**
 * The phrases a screen actually shows, newest first and each string once.
 *
 * `wins()` dedupes by `item.id`, because that is the right key for what it
 * returns: the same pattern winning twice is one win, and `Win.itemId` is
 * what a caller would use to tell two wins apart. What gets rendered is a
 * list of `<li>` keyed by the string itself, and two different items can
 * share a corrected exemplar -- "I saw a dog" is a plausible fix for more
 * than one mistake -- so the rendering list needs its own, text-level
 * dedupe rather than inheriting the item-level one.
 */
function distinctPhrases(wins: readonly Win[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const w of wins) {
    if (seen.has(w.phrase)) continue;
    seen.add(w.phrase);
    out.push(w.phrase);
  }
  return out;
}

/**
 * What a child sees: what they did, and what they have started saying well.
 *
 * Built only from `USED_CORRECTLY`. Spec §3.
 */
export function kidProgress(sessions: readonly ProgressSession[], now: Date): KidProgress {
  return {
    conversations: sessions.length,
    streak: conversationStreak(sessions, now),
    // Capped after deduping: capping first can shorten the list below
    // `KID_PHRASE_CAP` when a repeated phrase would otherwise have been
    // dropped for free.
    phrases: distinctPhrases(wins(sessions)).slice(0, KID_PHRASE_CAP),
  };
}

/**
 * What an adult sees: the same encouraging read, plus the one the engine was
 * actually built to produce.
 */
export function adultProgress(
  items: readonly PracticeItemRow[],
  sessions: readonly ProgressSession[],
  now: Date,
): AdultProgress {
  const all = struggles(items, sessions);
  return {
    conversations: sessions.length,
    streak: conversationStreak(sessions, now),
    phrases: distinctPhrases(wins(sessions)),
    struggles: all.slice(0, WORKING_ON_CAP),
    moreStruggles: Math.max(0, all.length - WORKING_ON_CAP),
  };
}
